import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme, css } from 'styled-components';
import { withValue } from '@g4/automation-hooks';
import moment from '@g4/moment-utc';
import tracking from '@g4/g4-analytics';
import { Text, Box, Media, Spinner } from '../../../../components/Boneyard';
import Price from '@g4/next-furniture/Price/Price';
import TabTooltip from './dayTab/TabTooltip';
import { DAY_TAB_LABEL_CONTENT } from '../../../../../automationHooks.json';
import { useTranslations } from '../../../../graphql/components/TranslationsProvider';
const StyledBox = styled(Box)`
  height: 100%;
  white-space: nowrap;
  margin: 0 2.5px 0 2.5px;
  flex-basis: auto;
  ${props =>
    props.active
      ? css`
          background-image: ${props.theme.colors.flights.flightItem.selected};
          transform: scale(1.05);
          transition: transform 0.3s ease-out;
        `
      : css`
          border: solid 2px ${props.theme.colors.common.disabled};
        `};
  ${props =>
    props.invalidState &&
    props.active &&
    `
    background-image: ${
      props.invalidState.type === 'warning'
        ? props.theme.colors.flights.flightItem.selected
        : 'none'
    };
    border: solid 2px ${
      props.invalidState.type === 'warning' ? '#01579b' : '#b71c1c'
    };
    `}
  ${props => props.theme.query.md} {
    border-bottom: 0;
  }
`;
const TabBox = styled.div`
  display: inline-block;
  position: relative;
  height: ${props => props.tabHeight}px;
`;
const DayTab = ({
  isTabLoading,
  invalidState,
  date,
  theme,
  active,
  disabled,
  priceOfCheapestFlight,
  automationHook,
  tabPosition,
}) => {
  const translations = useTranslations();
  const { noFlightsStateText } = translations.flightsPage;
  const errorTranslations = translations.flightsErrors;
  const [lastError, setLastError] = useState('');
  useEffect(() => {
    const timeOut = setTimeout(() => {
      if (invalidState && active) {
        tracking.trackEvent(
          'client_side_error',
          errorTranslations[invalidState.key]
        );
      }
    }, 100);
    return () => clearTimeout(timeOut);
  }, [invalidState, active, errorTranslations]);
  if (
    invalidState &&
    active &&
    invalidState.key !== lastError &&
    invalidState.type === 'error'
  ) {
    setLastError(invalidState.key);
  }
  if (!invalidState && lastError) {
    setLastError('');
  }
  return (
    <Media>
      {mq => {
        const isDesktop = mq.gte('lg');
        const isTablet = mq.gt('sm') && mq.lt('lg');
        return (
          <TabBox tabHeight={mq.gt('sm') ? 90 : 65}>
            {invalidState && active && (
              <TabTooltip
                role="tooltip"
                id={date + '-tooltip'}
                tabHeight={mq.gt('sm') ? 90 : 65}
                tabPosition={tabPosition}
                backgroundColor={
                  invalidState.type === 'warning' ? '#01579b' : '#b71c1c'
                }
                invalidState={invalidState}
                textColor={'#fff'}
                width={260}
              />
            )}
            <StyledBox
              backgroundColor={
                disabled
                  ? theme.colors.flights.flightItem.divider
                  : theme.colors.flights.flightItem.lightBackground
              }
              borderRadius={isDesktop || isTablet ? [8, 8, 0, 0] : [8, 8, 8, 8]}
              pt={isDesktop ? 3 : 1}
              pr={isDesktop ? 2 : 1}
              pl={isDesktop ? 2 : 1}
              active={active}
              flex={'1'}
              automationHook={automationHook}
              invalidState={invalidState}
            >
              <Box
                d={'flex'}
                direction={'row'}
                justifyContent={'space-between'}
                pb={2}
                pr={isDesktop ? 1 : 0}
                pl={isDesktop ? 1 : 0}
              >
                {!active ? (
                  <>
                    <Text
                      size={isDesktop ? 16 : 14}
                      color={theme.colors.flights.flightItem.lightBlue}
                      weight={500}
                      automationHook={withValue(
                        DAY_TAB_LABEL_CONTENT.hook,
                        'short-day-of-the-week_' +
                          moment(date).format('YYYY-MM-DD')
                      )}
                    >
                      {moment(date).format('ddd')}
                    </Text>
                    <Text
                      size={isDesktop ? 16 : 14}
                      color={theme.colors.flights.flightItem.lightBlue}
                      weight={500}
                      automationHook={withValue(
                        DAY_TAB_LABEL_CONTENT.hook,
                        'day-of-the-month_' + moment(date).format('YYYY-MM-DD')
                      )}
                    >
                      {moment(date).format('DD')}
                    </Text>
                  </>
                ) : (
                  <Text
                    color={theme.colors.flights.flightItem.lightBlue}
                    weight={500}
                    size={isDesktop ? 16 : 14}
                    automationHook={withValue(
                      DAY_TAB_LABEL_CONTENT.hook,
                      'long-day-of-the-week_' +
                        moment(date).format('YYYY-MM-DD')
                    )}
                  >
                    {moment(date).format('dddd')}
                  </Text>
                )}
              </Box>
              <Box
                d={'flex'}
                justifyContent={'flex-end'}
                h={'50%'}
                alignItems={'center'}
              >
                {active ? (
                  <Text
                    size={isDesktop ? 30 : 20}
                    color={theme.colors.flights.flightItem.lightBlue}
                    weight={600}
                    automationHook={withValue(
                      DAY_TAB_LABEL_CONTENT.hook,
                      'month-and-day_' + moment(date).format('YYYY-MM-DD')
                    )}
                  >
                    {moment(date).format('MMM DD')}
                  </Text>
                ) : disabled ? (
                  <Text
                    size={isDesktop ? 16 : 14}
                    color={theme.colors.flights.flightItem.lightBlue}
                    weight={500}
                    automationHook={withValue(
                      DAY_TAB_LABEL_CONTENT.hook,
                      'no-flights_' + moment(date).format('YYYY-MM-DD')
                    )}
                  >
                    {noFlightsStateText}
                  </Text>
                ) : isTabLoading ? (
                  <Spinner
                    size="xsmall"
                    appearance="dark"
                    automationHook={withValue('day-tab', 'spinner')}
                  />
                ) : (
                  <Price
                    currencyFontSize={isDesktop ? 18 : 12}
                    decimalFontSize={isDesktop ? 16 : 12}
                    priceFontSize={isDesktop ? 30 : 20}
                    value={priceOfCheapestFlight}
                    color={theme.colors.flights.flightItem.lightBlue}
                    automationHook={withValue(
                      DAY_TAB_LABEL_CONTENT.hook,
                      'flight-price_' + moment(date).format('YYYY-MM-DD')
                    )}
                  />
                )}
              </Box>
            </StyledBox>
          </TabBox>
        );
      }}
    </Media>
  );
};
DayTab.propTypes = {
  isTabLoading: PropTypes.bool,
  onClick: PropTypes.func,
  theme: PropTypes.object,
  automationHook: PropTypes.string,
  date: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  priceOfCheapestFlight: PropTypes.number,
  invalidState: PropTypes.object,
  tabPosition: PropTypes.string,
};
export default withTheme(DayTab);
